import React from 'react';

/** Shared page width tiers — keeps hub vs data vs form layouts consistent. */
export type PageWidth = 'narrow' | 'standard' | 'wide' | 'full';

const WIDTH_CLASS: Record<PageWidth, string> = {
  narrow: 'max-w-3xl',
  standard: 'max-w-6xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

type PageShellProps = {
  width?: PageWidth;
  bg?: 'white' | 'bone';
  className?: string;
  innerClassName?: string;
  children: React.ReactNode;
};

/**
 * Centers page content at a consistent max width with responsive horizontal padding.
 *
 * Background tiers:
 * - `white` (default) — standard app pages: tables, hubs, settings, forms
 * - `bone` — dashboard home (Launchpad) and multi-pane workspaces only
 *
 * Use `full` width for data tables; `standard` for hub pages; `narrow` for forms.
 */
export function PageShell({
  width = 'standard',
  bg = 'white',
  className = '',
  innerClassName = '',
  children,
}: PageShellProps) {
  return (
    <div
      className={`flex min-h-full min-w-0 flex-1 flex-col ${
        bg === 'bone' ? 'bg-bone' : 'bg-white'
      } ${className}`}
    >
      <div
        className={`${WIDTH_CLASS[width]} mx-auto w-full min-w-0 px-4 py-6 sm:px-6 lg:px-8 ${innerClassName}`}
      >
        {children}
      </div>
    </div>
  );
}

export const PAGE_WIDTH_CLASS = WIDTH_CLASS;
